import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { brandKeys, type BrandListQuery } from '@/services/queryKeys';
import { brandService } from '@/services/brand.service';
import type { CreateBrandInput, UpdateBrandInput } from '@tcr-erp/shared';

/**
 * BE GAP — service throws BeGapError. Pages using these hooks should
 * detect the error in `useQuery.error` and render a placeholder.
 */
export function useBrandList(query: BrandListQuery = {}) {
  return useQuery({
    queryKey: brandKeys.list(query),
    queryFn: () => brandService.list(query),
    retry: 0,
  });
}

export const PRODUCT_BRANDS_PAGE_QUERY: BrandListQuery = {
  q: undefined,
  page: 1,
  page_size: 50,
};

export function useBrandDetail(id: string | undefined) {
  return useQuery({
    queryKey: brandKeys.detail(id ?? '__none__'),
    queryFn: () => brandService.getById(id!),
    enabled: !!id,
    retry: 0,
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBrandInput) => brandService.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: brandKeys.all }),
  });
}

export function useUpdateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateBrandInput }) =>
      brandService.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: brandKeys.all }),
  });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => brandService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: brandKeys.all }),
  });
}
